import * as React from "react";
import { Calendar as CalendarIcon, X as ClearIcon, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  parseLocalDate,
  formatLocalDate,
  formatTRDate,
  isValidDateString,
  getTurkishHolidays,
} from "@/utils/dateUtils";

// YYYY-MM-DD -> GG.AA.YYYY (elle giriş alanlarında gösterim)
const ymdToTR = (ymd) =>
  ymd && isValidDateString(ymd) ? formatTRDate(ymd, { short: true }) : "";

// Elle yazılan "GG.AA.YYYY" (. / - ayraçları) -> YYYY-MM-DD. Geçersizse "".
const trTextToYmd = (text) => {
  if (!text) return "";
  const m = /^(\d{1,2})[.,/-](\d{1,2})[.,/-](\d{4})$/.exec(text.trim());
  if (!m) return "";
  const ymd = `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return isValidDateString(ymd) ? ymd : "";
};

/**
 * Tarih aralığı seçimi için ortak component. Tek bir Popover içinden
 * react-day-picker'ın range modu ile başlangıç-bitiş seçilir.
 *
 * Props:
 *   startDate / endDate: "YYYY-MM-DD" string veya ""
 *   onChange: ({ start, end }) => void
 *   min, max: "YYYY-MM-DD" string (opsiyonel)
 *   label: string (opsiyonel)
 *   placeholder: string (varsayılan: "Başlangıç ve bitiş tarihi seçin")
 *   helperText: string (opsiyonel)
 *   error: string (opsiyonel)
 *   disabled: boolean
 *   required: boolean
 *   clearable: boolean (varsayılan true)
 *   manualInput: boolean (varsayılan true) — elle (klavyeyle) tarih girişi alanları + takvim butonu gösterir
 *   numberOfMonths: 1 veya 2 (varsayılan 2)
 *   highlightHolidays: boolean
 *   align: PopoverContent align
 *   buttonClassName: ek class
 *   id
 */
const DateRangePicker = React.forwardRef(function DateRangePicker(
  {
    startDate = "",
    endDate = "",
    onChange,
    min,
    max,
    label,
    placeholder = "Başlangıç ve bitiş tarihi seçin",
    helperText,
    error,
    disabled = false,
    required = false,
    clearable = true,
    manualInput = true,
    numberOfMonths = 2,
    highlightHolidays = false,
    align = "start",
    buttonClassName,
    id,
    ...rest
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);

  const selectedRange = React.useMemo(() => {
    const from = startDate && isValidDateString(startDate) ? parseLocalDate(startDate) : undefined;
    const to = endDate && isValidDateString(endDate) ? parseLocalDate(endDate) : undefined;
    return { from, to };
  }, [startDate, endDate]);

  const minDate = React.useMemo(
    () => (min && isValidDateString(min) ? parseLocalDate(min) : undefined),
    [min],
  );
  const maxDate = React.useMemo(
    () => (max && isValidDateString(max) ? parseLocalDate(max) : undefined),
    [max],
  );

  const disabledMatcher = React.useMemo(() => {
    const matchers = [];
    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers.length ? matchers : undefined;
  }, [minDate, maxDate]);

  // Takvimde gösterilen ay (kontrollü) — elle yazılan/seçilen başlangıca atlar
  const [month, setMonth] = React.useState(
    () => (startDate && isValidDateString(startDate) ? parseLocalDate(startDate) : (minDate || new Date())),
  );
  React.useEffect(() => {
    if (startDate && isValidDateString(startDate)) {
      setMonth(parseLocalDate(startDate));
    }
  }, [startDate]);

  // Caption'daki ay/yıl açılır menüsü için yıl sınırları (hızlı yıl atlama)
  const fromYear = minDate ? minDate.getFullYear() : 2015;
  const toYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 5;

  const modifiers = React.useMemo(() => {
    if (!highlightHolidays) return undefined;
    const years = new Set();
    const today = new Date();
    years.add(today.getFullYear());
    if (selectedRange.from) years.add(selectedRange.from.getFullYear());
    if (selectedRange.to) years.add(selectedRange.to.getFullYear());
    if (minDate) years.add(minDate.getFullYear());
    if (maxDate) years.add(maxDate.getFullYear());
    const holidayDates = [];
    years.forEach((y) => {
      (getTurkishHolidays(y) || []).forEach((iso) => {
        if (isValidDateString(iso)) holidayDates.push(parseLocalDate(iso));
      });
    });
    return { holiday: holidayDates };
  }, [highlightHolidays, selectedRange, minDate, maxDate]);

  const modifiersClassNames = React.useMemo(
    () => (highlightHolidays ? { holiday: "text-red-600 font-semibold" } : undefined),
    [highlightHolidays],
  );

  const handleSelect = React.useCallback(
    (range) => {
      const start = range?.from ? formatLocalDate(range.from) : "";
      const end = range?.to ? formatLocalDate(range.to) : "";
      onChange?.({ start, end });
      // Aralık tamamlandığında (başlangıç + bitiş seçildi) takvimi otomatik kapat;
      // kullanıcı boş alana tıklamak zorunda kalmasın.
      if (range?.from && range?.to) {
        setOpen(false);
      }
    },
    [onChange],
  );

  const handleClear = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      onChange?.({ start: "", end: "" });
    },
    [onChange],
  );

  // Elle (klavyeyle) tarih girişi: kullanıcı serbestçe yazsın diye yerel metin
  // state'i tutulur; geçerli tarihe ulaşınca veya boşalınca onChange tetiklenir.
  const [startText, setStartText] = React.useState(ymdToTR(startDate));
  const [endText, setEndText] = React.useState(ymdToTR(endDate));
  const startFocused = React.useRef(false);
  const endFocused = React.useRef(false);

  // Dışarıdan değer değişince (takvim seçimi vb.) ve alan odakta değilse senkronla
  React.useEffect(() => {
    if (!startFocused.current) setStartText(ymdToTR(startDate));
  }, [startDate]);
  React.useEffect(() => {
    if (!endFocused.current) setEndText(ymdToTR(endDate));
  }, [endDate]);

  const handleStartText = React.useCallback(
    (e) => {
      const text = e.target.value;
      setStartText(text);
      const ymd = trTextToYmd(text);
      if (ymd) onChange?.({ start: ymd, end: endDate });
      else if (text.trim() === "") onChange?.({ start: "", end: endDate });
    },
    [onChange, endDate],
  );
  const handleEndText = React.useCallback(
    (e) => {
      const text = e.target.value;
      setEndText(text);
      const ymd = trTextToYmd(text);
      if (ymd) onChange?.({ start: startDate, end: ymd });
      else if (text.trim() === "") onChange?.({ start: startDate, end: "" });
    },
    [onChange, startDate],
  );
  // Odak çıkışında metni kanonik biçime (veya son geçerli değere) sabitle
  const handleStartBlur = React.useCallback(() => {
    startFocused.current = false;
    setStartText(ymdToTR(startDate));
  }, [startDate]);
  const handleEndBlur = React.useCallback(() => {
    endFocused.current = false;
    setEndText(ymdToTR(endDate));
  }, [endDate]);

  const displayLabel = React.useMemo(() => {
    if (startDate && endDate && isValidDateString(startDate) && isValidDateString(endDate)) {
      return `${formatTRDate(startDate, { short: true })} - ${formatTRDate(endDate, { short: true })}`;
    }
    if (startDate && isValidDateString(startDate)) {
      return `${formatTRDate(startDate, { short: true })} - Bitiş seçin`;
    }
    return placeholder;
  }, [startDate, endDate, placeholder]);

  const hasValue = Boolean(
    (startDate && isValidDateString(startDate)) || (endDate && isValidDateString(endDate)),
  );
  const hasError = Boolean(error);

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={id} className="text-sm">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        {manualInput ? (
          <div
            className={cn(
              "mt-1 flex flex-wrap items-center gap-1 rounded-md border bg-background px-1",
              hasError ? "border-red-500 bg-red-50" : "border-input",
              !hasValue && required && !hasError && "border-yellow-400 bg-yellow-50",
              buttonClassName,
            )}
          >
            <input
              id={id}
              ref={ref}
              type="text"
              inputMode="numeric"
              disabled={disabled}
              placeholder="GG.AA.YYYY"
              value={startText}
              onFocus={() => { startFocused.current = true; }}
              onChange={handleStartText}
              onBlur={handleStartBlur}
              className="h-8 w-[92px] shrink-0 bg-transparent px-1 text-sm focus:outline-none disabled:cursor-not-allowed"
              aria-label="Başlangıç tarihi"
              {...rest}
            />
            <span className="text-gray-400">–</span>
            <input
              type="text"
              inputMode="numeric"
              disabled={disabled}
              placeholder="GG.AA.YYYY"
              value={endText}
              onFocus={() => { endFocused.current = true; }}
              onChange={handleEndText}
              onBlur={handleEndBlur}
              className="h-8 w-[92px] shrink-0 bg-transparent px-1 text-sm focus:outline-none disabled:cursor-not-allowed"
              aria-label="Bitiş tarihi"
            />
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                className="h-7 w-7 shrink-0"
                aria-label="Takvimden seç"
              >
                <CalendarIcon className="h-4 w-4 text-blue-600" />
              </Button>
            </PopoverTrigger>
            {clearable && hasValue && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="mr-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                aria-label="Tarih aralığını temizle"
              >
                <ClearIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        ) : (
          <PopoverTrigger asChild>
            <Button
              id={id}
              ref={ref}
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start text-left font-normal mt-1",
                !hasValue && "text-muted-foreground",
                !hasValue && required && !hasError && "border-yellow-400 bg-yellow-50",
                hasError && "border-red-500 bg-red-50",
                buttonClassName,
              )}
              {...rest}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-blue-600 shrink-0" />
              <span className="truncate flex-1">{displayLabel}</span>
              {clearable && hasValue && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleClear(e);
                  }}
                  className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                  aria-label="Tarih aralığını temizle"
                >
                  <ClearIcon className="h-3 w-3" />
                </span>
              )}
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent className="w-auto p-0" align={align}>
          <Calendar
            mode="range"
            selected={selectedRange}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            {...(manualInput
              ? {
                  month,
                  onMonthChange: setMonth,
                  captionLayout: "dropdown-buttons",
                  fromYear,
                  toYear,
                }
              : { defaultMonth: selectedRange.from || minDate || new Date() })}
            disabled={disabledMatcher}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            classNames={{
              caption_dropdowns: "flex items-center justify-center gap-1",
              // Native <select> zaten ay/yıl değerini gösteriyor; rdp'nin ayrıca
              // çizdiği değer+ok kopyasını (caption_label) gizle → tekrar önlenir.
              caption_label: "hidden",
              dropdown:
                "rounded-md border border-input bg-white px-1 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer",
              dropdown_month: "relative",
              dropdown_year: "relative",
              day_selected:
                "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-700 focus:text-white",
              day_range_start:
                "day-range-start bg-blue-700 text-white hover:bg-blue-800",
              day_range_end:
                "day-range-end bg-blue-700 text-white hover:bg-blue-800",
              day_range_middle:
                "aria-selected:bg-blue-300 aria-selected:text-blue-900",
              day_today: "bg-blue-100 text-blue-900 font-bold",
            }}
          />
          {/* Açık kapatma: aralık seçilince otomatik kapanır; kullanıcı isterse
              (ör. tek gün / erken çıkış) buradan da kapatabilsin — dışarı
              tıklamak zorunda kalmasın. */}
          <div className="flex items-center justify-end gap-2 border-t px-3 py-2">
            {hasValue && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-gray-600"
                onClick={() => onChange?.({ start: "", end: "" })}
              >
                Temizle
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              className="h-8 bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setOpen(false)}
            >
              Tamam
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {helperText && !hasError && (
        <p className="text-xs text-gray-500 mt-1">{helperText}</p>
      )}
      {hasError && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
});

export { DateRangePicker };
export default DateRangePicker;
